import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import PropTypes from 'prop-types'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import { Divider } from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import Button from '@app/stories/Button'
import Container from '@app/stories/Container'
import Section from '@app/stories/Section'
import Typography from '@app/stories/Typography'
import { scrollToHash } from '@app/libs/scroll'
import FormattedText from '@app/stories/FormatFromDB'
import YoutubeEmbed from '@app/components/Embeds/YoutubeEmbed'

import { useWhiteLabelingContext } from '@app/context/WhiteLabelingContext'

const NetworkHero = ({ landerData, networkSlug, networkData }) => {
  const navigate = useNavigate()
  const [heroImageLink, setHeroImageLink] = useState(null)

  const { planTheme } = networkData || {}

  const {
    heroTitle,
    heroSubText,
    heroImage,
    heroBackgroundColor,
    ctaButton,
    ctaText,
    featuredServices,
    displayFeaturedServices,
    hideInfoBoxes,
    hideYouShouldKnow,
    videoEmbedID,
  } = landerData

  const { planColorPrimary } = useWhiteLabelingContext()

  useEffect(() => {
    if (Array.isArray(heroImage)) {
      setHeroImageLink(heroImage[0]?.downloadURL)
    } else {
      setHeroImageLink(null)
    }
  }, [heroImage])

  const quickLinkPresent = landerData?.quickLinks && Object.keys(landerData.quickLinks).length !== 0

  const handleAlignText = (text) => {
    if (text === 'center') {
      return 'center'
    } else if (text === 'right') {
      return 'flex-end'
    }
    return 'flex-start'
  }
  const heroTextAlign = handleAlignText(planTheme?.heroTextAlign)

  const handleHeroBoxBackgroundColor = (theme) => {
    if (theme?.heroBoxBackgroundColor) {
      if (theme?.heroBoxBackgroundOpacity) {
        const opacityHex = Math.round(theme.heroBoxBackgroundOpacity * 255)
          .toString(16)
          .padStart(2, '0')
        return `${theme.heroBoxBackgroundColor}${opacityHex}`
      } else {
        return theme.heroBoxBackgroundColor
      }
    }
    return 'transparent'
  }

  return (
    <Section
      variant='noPadding'
      style={{
        backgroundColor: `${heroBackgroundColor || '#fff'}`,
        paddingTop: quickLinkPresent ? '56px' : '0px',
      }}
      sx={{
        paddingBottom: {
          xs: '0px',
          md: '36px',
        },
      }}
    >
      <Box
        sx={{
          display: { xs: 'none', md: 'block' },
        }}
      >
        <Box
          sx={{
            backgroundImage: planTheme?.makeImageBackground ? `url(${heroImageLink})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            display: 'flex',
            justifyContent: 'center',
            height: '600px',
          }}
        >
          <Box
            maxWidth='1536px'
            width='100%'
            sx={{
              padding: {
                xs: '0px 16px',
                md: '0px 24px',
              },
            }}
            display='flex'
            flexDirection={planTheme?.makeImageBackground ? 'column' : 'row'}
            justifyContent={planTheme?.makeImageBackground ? 'center' : 'space-between'}
            alignItems={planTheme?.makeImageBackground ? heroTextAlign : 'center'}
          >
            <Box
              maxWidth={500}
              padding='16px'
              display='flex'
              flexDirection='column'
              justifyContent='center'
              alignItems={heroTextAlign}
              backgroundColor={handleHeroBoxBackgroundColor(planTheme)}
              borderRadius='8px'
            >
              {heroTitle && (
                <Typography
                  variant='h1'
                  gutterBottom
                  weight='medium'
                  align={planTheme?.heroTextAlign || 'left'}
                  style={{
                    fontFamily: 'Poppins',
                    fontSize: planTheme?.heroTitle?.fontSize || '2.5rem',
                    color: planTheme?.heroTitle?.fontColor || '#000',
                    fontWeight: planTheme?.heroTitle?.fontWeight || '500',
                  }}
                >
                  {heroTitle}
                </Typography>
              )}
              {heroSubText && (
                <FormattedText
                  text={heroSubText}
                  textType='body1'
                  weight='light'
                  align={planTheme?.heroTextAlign || 'left'}
                  style={{
                    fontFamily: 'Poppins',
                    fontSize: planTheme?.heroSubText?.fontSize || '1rem',
                    color: planTheme?.heroSubText?.fontColor || '#000',
                    fontWeight: planTheme?.heroSubText?.fontWeight || '300',
                  }}
                />
              )}
              {ctaButton && (
                <Box mt={3}>
                  <Button
                    display='standard'
                    onClick={() => {
                      scrollToHash('#specialties')
                    }}
                    size='large'
                    style={{
                      backgroundColor: planColorPrimary,
                      '&:hover': {
                        backgroundColor: `${planColorPrimary}` || '#652d92',
                      },
                      '&.Mui-focused': {
                        backgroundColor: `${planColorPrimary}` || '#652d92',
                      },
                    }}
                  >
                    {ctaText}
                  </Button>
                </Box>
              )}
            </Box>
            <Box width='50%'>
              {!planTheme?.makeImageBackground && (heroImageLink || videoEmbedID) && (
                <>
                  {videoEmbedID ? (
                    <YoutubeEmbed embedID={videoEmbedID} width='100%' />
                  ) : (
                    <img
                      src={heroImageLink}
                      alt='Network Hero'
                      width='100%'
                      style={{ borderRadius: '8px' }}
                    />
                  )}
                </>
              )}
            </Box>
          </Box>
        </Box>
        <Container>
          {!hideInfoBoxes && (
            <>
              <Box mt={planTheme?.makeImageBackground ? 5 : 3} mb={1} width='100%'>
                <Divider />
              </Box>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={12} md={3} lg={3}>
                  <Box width='100%' padding='20px'>
                    <Typography variant='h5'>No insurance needed</Typography>
                    We contract directly with our clinician partners so you can access quality care
                    without hassles or surprises.
                  </Box>
                </Grid>
                <Grid item xs={12} sm={12} md={3} lg={3}>
                  <Box width='100%' padding='20px'>
                    <Typography variant='h5'>Concierge support</Typography>
                    Our case managers are just a call or text away to help coordinate appointment
                    scheduling and your treatment plan.
                  </Box>
                </Grid>
                <Grid item xs={12} sm={12} md={3} lg={3}>
                  <Box width='100%' padding='20px'>
                    <Typography variant='h5'>US-licensed clinicians</Typography>
                    We partner with accredited clinicians and board-certified specialists.
                  </Box>
                </Grid>
                <Grid item xs={12} sm={12} md={3} lg={3}>
                  <Box width='100%' padding='20px'>
                    <Typography variant='h5'>Your health data is secure</Typography>
                    We partner with leading technology companies to keep your information secure and
                    protected by law.
                  </Box>
                </Grid>
              </Grid>
            </>
          )}
          {!hideYouShouldKnow && (
            <>
              <Box mt={1} mb={5} width='100%'>
                <Divider />
              </Box>
              <Box mt={3} width='100%'>
                <Grid
                  container
                  alignItems='center'
                  width='100%'
                  style={{
                    boxShadow: '0px 0px 10px 0px rgba(0,0,0,0.1)',
                    borderRadius: '5px',
                    backgroundColor: landerData.yskBgColor || '#f5f5f5',
                    padding: '15px',
                  }}
                >
                  <Typography variant='h5' color='textPrimary'>
                    You should know
                  </Typography>
                  <Box width='100%' alignItems='center' display='flex'>
                    <Typography variant='body2' weight='light'>
                      Based on your health benefits, your service may be reimbursable by your
                      insurance plan. Check with your case manager to determine eligibility after
                      you receive your service. If eligible, your case manager will provide you with
                      the appropriate documentation needed. Mishe does not accept insurance at this
                      time, and is not intended for beneficiaries of government payor programs,
                      including Medicare and Medicaid.
                    </Typography>
                  </Box>
                </Grid>
              </Box>
            </>
          )}
        </Container>
      </Box>
      <Box
        sx={{
          display: { xs: 'block', md: 'none' },
        }}
      >
        <Box variant='landing' paddingTop='8px'>
          <Box
            sx={{
              backgroundImage: planTheme?.makeImageBackground ? `url(${heroImageLink})` : 'none',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              height: '600px',
              padding: {
                xs: '0px 16px',
              },
            }}
          >
            {!planTheme?.makeImageBackground && (heroImageLink || videoEmbedID) && (
              <Box>
                {videoEmbedID ? (
                  <YoutubeEmbed embedID={videoEmbedID} width='100%' />
                ) : (
                  <img src={heroImageLink} alt='Network Hero' width='100%' />
                )}
              </Box>
            )}
            <Box
              maxWidth={500}
              padding='16px'
              display='flex'
              flexDirection='column'
              justifyContent='center'
              backgroundColor={handleHeroBoxBackgroundColor(planTheme)}
              borderRadius='8px'
              marginTop={2}
              marginBottom={2}
            >
              {heroTitle && (
                <Typography
                  variant='h1'
                  align='center'
                  weight='medium'
                  style={{
                    fontFamily: 'Poppins',
                    fontSize: planTheme?.heroTitle?.fontSize || '2.5rem',
                    color: planTheme?.heroTitle?.fontColor || '#000',
                    marginBottom: '0px',
                  }}
                >
                  {heroTitle}
                </Typography>
              )}
              {heroSubText && (
                <FormattedText
                  text={heroSubText}
                  textType='body1'
                  weight='light'
                  align='center'
                  style={{
                    fontFamily: 'Poppins',
                    fontSize: planTheme?.heroSubText?.fontSize || '1rem',
                    color: planTheme?.heroSubText?.fontColor || '#000',
                  }}
                />
              )}
              {ctaButton && (
                <Box display='flex' justifyContent='center' mt={2}>
                  <Button
                    display='standard'
                    onClick={() => {
                      scrollToHash('#specialties')
                    }}
                    size='large'
                    style={{
                      backgroundColor: planColorPrimary,
                      '&:hover': {
                        backgroundColor: `${planColorPrimary}` || '#652d92',
                      },
                      '&.Mui-focused': {
                        backgroundColor: `${planColorPrimary}` || '#652d92',
                      },
                    }}
                  >
                    {ctaText}
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
          <Container>
            <Box padding='16px 8px 0px 8px'>
              {!hideInfoBoxes && (
                <>
                  <Divider style={{ margin: '32px 0px' }} />
                  <Box width='100%' display='flex'>
                    <CheckIcon fontSize='small' style={{ color: '#303030', marginRight: '10px' }} />
                    <Typography variant='body2' weight='light'>
                      No insurance needed
                    </Typography>
                  </Box>
                  <Box mt={1} width='100%' display='flex'>
                    <CheckIcon fontSize='small' style={{ color: '#303030', marginRight: '10px' }} />
                    <Typography variant='body2' weight='light'>
                      Concierge support
                    </Typography>
                  </Box>
                  <Box mt={1} width='100%' display='flex'>
                    <CheckIcon fontSize='small' style={{ color: '#303030', marginRight: '10px' }} />
                    <Typography variant='body2' weight='light'>
                      US-licensed clinicians
                    </Typography>
                  </Box>
                  <Box mt={1} width='100%' display='flex'>
                    <CheckIcon fontSize='small' style={{ color: '#303030', marginRight: '10px' }} />
                    <Typography variant='body2' weight='light'>
                      Your health data is secure
                    </Typography>
                  </Box>
                </>
              )}
              {!hideYouShouldKnow && (
                <>
                  <Divider style={{ margin: '32px 0px' }} />
                  <Typography variant='h5' color='textPrimary'>
                    You should know
                  </Typography>
                  <Box width='100%' alignItems='center' display='flex'>
                    <Typography variant='body2' weight='light'>
                      Based on your health benefits, your service may be reimbursable by your
                      insurance plan. Check with your case manager to determine eligibility after
                      you receive your service. If eligible, your case manager will provide you with
                      the appropriate documentation needed. Mishe does not accept insurance at this
                      time, and is not intended for beneficiaries of government payor programs,
                      including Medicare and Medicaid.
                    </Typography>
                  </Box>
                </>
              )}
              {(!hideInfoBoxes || !hideYouShouldKnow) && <Divider style={{ margin: '32px 0px' }} />}
            </Box>
          </Container>
        </Box>
      </Box>
    </Section>
  )
}

NetworkHero.propTypes = {
  landerData: PropTypes.objectOf(PropTypes.any).isRequired,
  networkSlug: PropTypes.string.isRequired,
}

export default NetworkHero
